import { hourTicks, stepForSpacing, TICK_STEPS } from './charts.ts';
import { trackChartPointer } from './chartPointer.ts';
import { gridLineBudget, gridTicks, tickDecimals } from './lib/gridTicks.ts';
import { positionChartTip } from './chartTip.ts';
import type { TideDay } from './sources/tides.ts';

/**
 * A whole day of predicted tide for one station.
 *
 * One series, so no legend box — the panel title names it. The highs and lows
 * are direct-labelled because they are the only values anyone reads off a tide
 * curve; everything between them is shape, and the table below carries the rest.
 */

const W = 336;
const PLOT_H = 104;
const PAD = { top: 16, right: 14, bottom: 20, left: 34 };

const INK_MUTED = '#898781';
const INK_PRIMARY = '#ffffff';
const GRID = '#2c2c2a';
const AXIS = '#383835';
const SURFACE = '#1a1a19';
const SERIES_1 = '#3987e5';

const SVG_NS = 'http://www.w3.org/2000/svg';

function el(name: string, attrs: Record<string, string | number> = {}): SVGElement {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

/**
 * "1:15a" rather than "1:15 AM" — the extremes sit close enough on a 36-hour
 * window that the full form would overlap its neighbours.
 */
export function compactTime(d: Date): string {
  const h = d.getHours();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(d.getMinutes()).padStart(2, '0')}${h < 12 ? 'a' : 'p'}`;
}

/** Linear interpolation between the 30-minute samples. */
export function levelAt(curve: { t: Date; v: number }[], at: Date): number | null {
  if (curve.length === 0) return null;
  const ms = at.getTime();
  if (ms <= curve[0].t.getTime()) return curve[0].v;
  if (ms >= curve[curve.length - 1].t.getTime()) return curve[curve.length - 1].v;

  for (let i = 1; i < curve.length; i++) {
    const a = curve[i - 1];
    const b = curve[i];
    if (ms <= b.t.getTime()) {
      const span = b.t.getTime() - a.t.getTime();
      const f = span === 0 ? 0 : (ms - a.t.getTime()) / span;
      return a.v + (b.v - a.v) * f;
    }
  }
  return null;
}

/** Renders -0.04 as "0.0" rather than the nonsense "-0.0". */
export function feet(v: number, decimals = 1): string {
  const s = v.toFixed(decimals);
  return s === `-${(0).toFixed(decimals)}` ? (0).toFixed(decimals) : s;
}

/**
 * Whether the water is making or dropping.
 *
 * Taken from the next high or low rather than from the slope, because near a
 * turn a lookahead straddles it: eleven minutes before a low, sampling half an
 * hour out lands on the far side and reports "rising" while the water is still
 * dropping. The next extreme is also how the answer is actually used.
 */
export function tideTrend(
  day: TideDay,
  at: Date = new Date(),
): 'rising' | 'falling' | null {
  const next = day.extremes.find((e) => e.t.getTime() > at.getTime());
  if (next) return next.kind === 'high' ? 'rising' : 'falling';

  // Past the last extreme in the window, fall back to the local slope.
  const here = levelAt(day.curve, at);
  const later = levelAt(day.curve, new Date(at.getTime() + 30 * 60_000));
  if (here === null || later === null) return null;
  return later >= here ? 'rising' : 'falling';
}

export function renderTideDay(host: HTMLElement, day: TideDay, now: Date = new Date()): void {
  host.textContent = '';

  const { curve, extremes } = day;
  if (curve.length < 2) {
    const empty = document.createElement('p');
    empty.className = 'chart-empty';
    empty.textContent = 'No tide prediction available.';
    host.append(empty);
    return;
  }

  const innerW = W - PAD.left - PAD.right;
  const t0 = curve[0].t.getTime();
  const t1 = curve[curve.length - 1].t.getTime();
  const span = Math.max(t1 - t0, 1);

  const lo = Math.min(...curve.map((p) => p.v));
  const hi = Math.max(...curve.map((p) => p.v));
  // Headroom for the two label lines above each high, kept clear of the "now"
  // caption that sits just above the plot.
  const yLo = Math.floor(lo - 0.8);
  const yHi = Math.ceil(hi + 1.4);

  const xAt = (ms: number) => PAD.left + ((ms - t0) / span) * innerW;
  const yAt = (v: number) => PAD.top + (1 - (v - yLo) / (yHi - yLo)) * PLOT_H;

  const totalH = PAD.top + PLOT_H + PAD.bottom;
  const svg = el('svg', {
    viewBox: `0 0 ${W} ${totalH}`,
    width: '100%',
    height: totalH,
    role: 'img',
    'aria-label': 'Predicted tide from three hours ago through the next 33 hours',
  }) as SVGSVGElement;

  // --- Frame: a grid at round heights, so a level can be read off the curve ---
  const ticks = gridTicks(yLo, yHi, gridLineBudget(PLOT_H));
  const decimals = tickDecimals(ticks);
  for (const v of ticks) {
    svg.append(el('line', {
      x1: PAD.left, x2: W - PAD.right, y1: yAt(v), y2: yAt(v),
      stroke: v === yLo ? AXIS : GRID, 'stroke-width': 1,
    }));
    const label = el('text', {
      x: PAD.left - 6, y: yAt(v) + 3.5,
      'text-anchor': 'end', fill: INK_MUTED, 'font-size': 9,
      'font-variant-numeric': 'tabular-nums',
    });
    label.textContent = v.toFixed(decimals);
    svg.append(label);
  }

  // MLLW is the chart datum, so zero is the line that actually matters.
  if (yLo < 0 && yHi > 0) {
    svg.append(el('line', {
      x1: PAD.left, x2: W - PAD.right, y1: yAt(0), y2: yAt(0),
      stroke: AXIS, 'stroke-width': 1,
    }));
    const zero = el('text', {
      x: PAD.left - 6, y: yAt(0) + 3.5,
      'text-anchor': 'end', fill: INK_MUTED, 'font-size': 9,
    });
    zero.textContent = '0';
    svg.append(zero);
  }

  // --- Hour ticks on the same rules as the wind charts ---
  const tickStep = stepForSpacing(span, innerW, 7, TICK_STEPS);
  const labelStep = stepForSpacing(span, innerW, 46, TICK_STEPS.filter((h) => h >= tickStep));

  for (const ms of hourTicks(t0, t1, tickStep)) {
    const tx = xAt(ms);
    const at = new Date(ms);
    const labelled = at.getHours() % labelStep === 0;

    if (labelled) {
      svg.append(el('line', {
        x1: tx, x2: tx, y1: PAD.top, y2: PAD.top + PLOT_H,
        stroke: GRID, 'stroke-width': 1,
      }));
    }

    svg.append(el('line', {
      x1: tx, x2: tx,
      y1: PAD.top + PLOT_H, y2: PAD.top + PLOT_H + (labelled ? 6 : 3),
      stroke: INK_MUTED, 'stroke-width': 1, opacity: labelled ? 1 : 0.75,
    }));

    if (!labelled) continue;
    const text = el('text', {
      x: tx, y: PAD.top + PLOT_H + 15,
      'text-anchor': 'middle', fill: INK_MUTED, 'font-size': 9,
    });
    // The window spans more than a day, so midnight names the day rather than
    // repeating "12 AM" and leaving tomorrow indistinguishable from today.
    text.textContent = at.getHours() === 0
      ? new Intl.DateTimeFormat([], { weekday: 'short' }).format(at)
      : new Intl.DateTimeFormat([], { hour: 'numeric' }).format(at);
    svg.append(text);
  }

  // --- The curve, with water filled beneath it ---
  const line = curve.map((p) => `${xAt(p.t.getTime())},${yAt(p.v)}`).join(' ');
  svg.append(el('polygon', {
    points: `${xAt(t0)},${yAt(yLo)} ${line} ${xAt(t1)},${yAt(yLo)}`,
    fill: SERIES_1, 'fill-opacity': 0.16,
  }));
  svg.append(el('polyline', {
    points: line, fill: 'none', stroke: SERIES_1, 'stroke-width': 2,
    'stroke-linejoin': 'round', 'stroke-linecap': 'round',
  }));

  // --- Highs and lows: the only values worth labelling directly ---
  for (const e of extremes) {
    const ex = xAt(e.t.getTime());
    const ey = yAt(e.heightFt);
    svg.append(el('circle', {
      cx: ex, cy: ey, r: 3.5, fill: SERIES_1, stroke: SURFACE, 'stroke-width': 2,
    }));

    const above = e.kind === 'high';
    const label = el('text', {
      x: Math.min(Math.max(ex, PAD.left + 16), W - PAD.right - 16),
      y: above ? ey - 8 : ey + 15,
      'text-anchor': 'middle', fill: INK_PRIMARY, 'font-size': 9, 'font-weight': 600,
    });
    label.textContent = `${feet(e.heightFt)}′`;
    svg.append(label);

    const time = el('text', {
      x: Math.min(Math.max(ex, PAD.left + 16), W - PAD.right - 16),
      y: above ? ey - 18 : ey + 25,
      'text-anchor': 'middle', fill: INK_MUTED, 'font-size': 8,
    });
    // Compact form: over 36 hours the extremes sit ~41px apart, and a full
    // "10:15 PM" is nearly that wide on its own.
    time.textContent = compactTime(e.t);
    svg.append(time);
  }

  // --- Now ---
  const nowMs = now.getTime();
  if (nowMs >= t0 && nowMs <= t1) {
    const nx = xAt(nowMs);
    svg.append(el('line', {
      x1: nx, x2: nx, y1: PAD.top, y2: PAD.top + PLOT_H,
      stroke: INK_PRIMARY, 'stroke-width': 1, opacity: 0.55,
    }));
    const level = levelAt(curve, now);
    if (level !== null) {
      svg.append(el('circle', {
        cx: nx, cy: yAt(level), r: 4,
        fill: INK_PRIMARY, stroke: SURFACE, 'stroke-width': 2,
      }));
    }
    const nowLabel = el('text', {
      x: Math.min(Math.max(nx, PAD.left + 12), W - PAD.right - 12),
      y: PAD.top - 5,
      'text-anchor': 'middle', fill: INK_MUTED, 'font-size': 8,
    });
    nowLabel.textContent = 'now';
    svg.append(nowLabel);
  }

  // --- Hover layer, matching the wind charts ---
  const crosshair = el('line', {
    y1: PAD.top, y2: PAD.top + PLOT_H,
    stroke: INK_MUTED, 'stroke-width': 1, opacity: 0,
  });
  const cursor = el('circle', {
    r: 4, fill: SERIES_1, stroke: SURFACE, 'stroke-width': 2, opacity: 0,
  });
  svg.append(crosshair, cursor);

  const wrap = document.createElement('div');
  wrap.className = 'chart-wrap';
  const tip = document.createElement('div');
  tip.className = 'chart-tip';
  tip.hidden = true;
  wrap.append(svg, tip);

  const tipFmt = new Intl.DateTimeFormat([], {
    weekday: 'short', hour: 'numeric', minute: '2-digit',
  });

  const show = (ms: number) => {
    const clamped = Math.min(Math.max(ms, t0), t1);
    const at = new Date(clamped);
    const level = levelAt(curve, at);
    if (level === null) return;

    const tx = xAt(clamped);
    crosshair.setAttribute('x1', String(tx));
    crosshair.setAttribute('x2', String(tx));
    crosshair.setAttribute('opacity', '1');
    cursor.setAttribute('cx', String(tx));
    cursor.setAttribute('cy', String(yAt(level)));
    cursor.setAttribute('opacity', '1');

    const trend = tideTrend(day, at);
    tip.innerHTML =
      `<strong>${tipFmt.format(at)}</strong>` +
      `<span>${feet(level)} ft above MLLW</span>` +
      (trend ? `<span>${trend}</span>` : '');
    tip.hidden = false;
    // Keep the readout off the crosshair so the cursor dot stays visible.
    positionChartTip(svg, wrap, tip, tx, { top: PAD.top, bottom: PAD.top + PLOT_H });
  };

  const hide = () => {
    crosshair.setAttribute('opacity', '0');
    cursor.setAttribute('opacity', '0');
    tip.hidden = true;
  };

  trackChartPointer(svg, {
    onMove: (clientX) => {
      const box = svg.getBoundingClientRect();
      const px = ((clientX - box.left) / box.width) * W;
      const frac = Math.min(Math.max((px - PAD.left) / innerW, 0), 1);
      show(t0 + frac * span);
    },
    onEnd: hide,
  });

  host.append(wrap);
}

/** The table twin: every high and low in the window, readable without the chart. */
export function renderTideTable(host: HTMLElement, day: TideDay): void {
  host.textContent = '';
  // The window crosses midnight, so the weekday has to travel with the time.
  const fmt = new Intl.DateTimeFormat([], {
    weekday: 'short', hour: 'numeric', minute: '2-digit',
  });

  const table = document.createElement('table');
  table.className = 'reading-table';
  table.innerHTML =
    '<thead><tr><th scope="col">Time</th><th scope="col">Tide</th>' +
    '<th scope="col">Height</th></tr></thead>';

  const body = document.createElement('tbody');
  for (const e of day.extremes) {
    const row = document.createElement('tr');
    row.innerHTML =
      `<td>${fmt.format(e.t)}</td>` +
      `<td>${e.kind === 'high' ? 'High' : 'Low'}</td>` +
      `<td>${e.heightFt.toFixed(2)} ft</td>`;
    body.append(row);
  }
  table.append(body);
  host.append(table);
}
